import { useEffect, useRef, useState } from "react";
import { useUsageLimit } from "../../../customhooks/useUsageLimit.js";
import { useAuth } from "../../Login/AuthContext.js";
import { CircularProgress } from "@mui/material";

import operationIcon from "../../../assets/math-operations.svg";
import numbersIcon from "../../../assets/numbers.png";
import glitterIcon from "../../../assets/glitter.svg";
import "./Columnquestions.css";
import PromptPreview from "../../CustomPrompt/PromptPreview.jsx";

function buildPromptPreview(operation, firstDigits, secondDigits, difficulty) {
  return `Create 20 ${difficulty} ${operation || "[operation]"} column questions with a ${firstDigits}-digit first number and a ${secondDigits}-digit second number.`;
}

function isDifficultyAvailable(operation, firstDigits, difficulty) {
  if (!operation) return true;
  if (difficulty === "medium" && operation === "subtraction") return firstDigits >= 2;
  if (difficulty === "hard" && operation === "addition") return firstDigits >= 2;
  if (difficulty === "hard" && operation === "subtraction") return firstDigits >= 3;
  return true;
}

const Columnquestions = ({ generateContentAndPdf, loading }) => {
  const [selectedOperation, setOperation] = useState(null);
  const [selectedDifficulty, setDifficulty] = useState("medium");
  const [firstNumberDigits, setFirstNumberDigits] = useState(4); // Default to 4 digits for the first number
  const [secondNumberDigits, setSecondNumberDigits] = useState(3);
  const submittingRef = useRef(false);

  const { isAuthenticated } = useAuth();
  const { remainingUses, isDisabled, incrementUsage } = useUsageLimit(
    "globalUsage",
    5,
    undefined,
    isAuthenticated
  );

  const [errors, setErrors] = useState({
    operationError: "",
    firstNumError: "",
    secondNumError: "",
    digitComparisonError: "", // New error for digit comparison
  });

  const promptPreview = buildPromptPreview(
    selectedOperation,
    firstNumberDigits,
    secondNumberDigits,
    selectedDifficulty
  );

  useEffect(() => {
    if (!isDifficultyAvailable(selectedOperation, Number(firstNumberDigits), selectedDifficulty)) {
      setDifficulty("easy");
    }
  }, [selectedOperation, firstNumberDigits, selectedDifficulty]);

  // Function to validate digit comparison
  const validateDigitComparison = (first, second) => {
    if (first && second && parseInt(first) < parseInt(second)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        digitComparisonError: "The first number must have more or equal digits than the second number.",
      }));
      return false;
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        digitComparisonError: "",
      }));
      return true;
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (loading || submittingRef.current) return;

    // Check if user is authenticated
    if (!isAuthenticated && isDisabled) {
      alert(
        "You have reached the maximum number of uses. Please sign up for free credits! 👉🏻"
      );
      return;
    }

    setErrors({
      operationError: "",
      firstNumError: "",
      secondNumError: "",
      digitComparisonError: "",
    });

    let hasError = false;

    if (!selectedOperation) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        operationError: "Please select an operation.",
      }));
      hasError = true;
    }
    if (firstNumberDigits < 1 || firstNumberDigits > 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        firstNumError:
          "Please enter a valid number of digits for the 1st number.",
      }));
      hasError = true;
    }
    if (secondNumberDigits < 1 || secondNumberDigits > 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        secondNumError:
          "Please enter a valid number of digits for the 2nd number.",
      }));
      hasError = true;
    }

    // Validate digit comparison
    if (!validateDigitComparison(firstNumberDigits, secondNumberDigits)) {
      hasError = true;
    }

    if (!hasError && !isDisabled) {
      submittingRef.current = true;
      try {
        if (!isAuthenticated) incrementUsage();
        await generateContentAndPdf({
          operation: selectedOperation,
          firstDigits: Number(firstNumberDigits),
          secondDigits: Number(secondNumberDigits),
          difficulty: selectedDifficulty,
        });
      } finally {
        submittingRef.current = false;
      }
    }
  };

  const changeone = (e) => {
    const value = e.target.value;
    if (value === "" || (value >= 1 && value <= 6)) {
      setFirstNumberDigits(value);
      if (value.trim() !== "") {
        setErrors((prevErrors) => ({ ...prevErrors, firstNumError: "" }));
        // Validate comparison when first number changes
        validateDigitComparison(value, secondNumberDigits);
      }
    }
  };

  const changetwo = (e) => {
    const value = e.target.value;
    if (value === "" || (value >= 1 && value <= 6)) {
      setSecondNumberDigits(value);
      if (value.trim() !== "") {
        setErrors((prevErrors) => ({ ...prevErrors, secondNumError: "" }));
        // Validate comparison when second number changes
        validateDigitComparison(firstNumberDigits, value);
      }
    }
  };

  return (
    <div className="column-questions">

      <PromptPreview text={promptPreview} />
      <div className="cq-controls-row">

        {/* Operation */}
        <div className="cq-field">
          <label htmlFor="operation" className="form-label">
            <img
              src={operationIcon}
              alt=""
              aria-hidden="true"
              style={{ width: "16px", height: "16px" }}
            />
            Operation
          </label>
          <select
            id="operation"
            className="operation-dropdown"
            value={selectedOperation || ""}
            onChange={(e) => setOperation(e.target.value)}
          >
            <option value="" disabled>
              Select Operation
            </option>
            <option value="addition">Addition</option>
            <option value="subtraction">Subtraction</option>
            {/* <option value="multiplication">Multiplication</option> */}
          </select>
          {errors.operationError && (
            <p className="error">{errors.operationError}</p>
          )}
        </div>

        {/* Difficulty */}
        <div className="cq-field">
          <label htmlFor="difficulty" className="form-label">
            Difficulty
          </label>
          <select
            id="difficulty"
            className="operation-dropdown"
            value={selectedDifficulty}
            onChange={(e) => setDifficulty(e.target.value)}
          >
            <option value="easy">Easy — no regrouping</option>
            <option
              value="medium"
              disabled={!isDifficultyAvailable(selectedOperation, Number(firstNumberDigits), "medium")}
            >
              Medium — one regroup
            </option>
            <option
              value="hard"
              disabled={!isDifficultyAvailable(selectedOperation, Number(firstNumberDigits), "hard")}
            >
              Hard — multiple regroups
            </option>
          </select>
        </div>

        {/* First Number Digits */}
        <div className="cq-field">
          <label htmlFor="first-number" className="form-label">
            <img
              src={numbersIcon}
              alt=""
              aria-hidden="true"
              style={{ width: "16px", height: "16px" }}
            />
            Digits in <strong>1st</strong> number
          </label>
          <input
            className="digits-input"
            id="first-number"
            type="number"
            value={firstNumberDigits}
            onChange={changeone}
            min="1"
            max="6"
          />
          {errors.firstNumError && (
            <p className="error-msg">{errors.firstNumError}</p>
          )}
        </div>

        {/* Second Number Digits */}
        <div className="cq-field">
          <label htmlFor="second-number" className="form-label">
            <img
              src={numbersIcon}
              alt=""
              aria-hidden="true"
              style={{ width: "16px", height: "16px" }}
            />
            Digits in <strong>2nd</strong> number
          </label>
          <input
            className="digits-input"
            id="second-number"
            type="number"
            value={secondNumberDigits}
            onChange={changetwo}
            min="0"
            max="6"
          />
          {errors.secondNumError && (
            <p className="error-msg">{errors.secondNumError}</p>
          )}
        </div>

        {/* Generate Button */}
        <div className="cq-field cq-field--action">
          <button
            className="generate-button"
            data-gtm-event="track-click"
            gtm-data-label="generated-questions-column-temp"
            onClick={submitHandler}
            disabled={
              firstNumberDigits < 1 ||
              secondNumberDigits < 1 ||
              loading ||
              parseInt(firstNumberDigits) < parseInt(secondNumberDigits)
            }
          >
            {loading ? (
              <CircularProgress
                size={18}
                color="inherit"
                style={{ marginRight: "8px" }}
              />
            ) : (
              <img
                src={glitterIcon}
                alt=""
                aria-hidden="true"
                style={{ width: "16px", height: "16px", marginRight: "5px" }}
              />
            )}
            {loading
              ? "Generating..."
              : isAuthenticated
              ? "Generate Questions"
              : `Generate (${remainingUses} remaining)`}
          </button>
        </div>

      </div>

      {/* Digit Comparison Error */}
      {errors.digitComparisonError && (
        <p className="error">{errors.digitComparisonError}</p>
      )}

      
    </div>
  );
};

export default Columnquestions;
